<template>
  <v-row>
    <v-col cols="12" class="pb-0">
      <v-card tile flat class="pa-2" id="documents-note-text">
        <v-card-text id="please-note-verbiage">
          {{ $t('L_please_note_that_some_documents') }}
          <div class="mt-4 font-weight-bold">
            {{ $t('L_if_you_do_not_see_a_copy_of_your_full_life_policy_it_sec') }} <br />
            {{ $t('L_if_you_do_not_see_a_copy_of_your_full_life_policy_it_third') }}
          </div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" class="pt-0">
      <v-card tile align-stretch flat>
        <v-card flat v-if="docs.length > 0">
          <v-data-table
            :headers="headers"
            :items="docs"
            hide-default-footer
            mobile-breakpoint="0"
            density="compact"
          >
            <template v-slot:item="props">
              <tr>
                <td>{{ props.item.date }}</td>
                <td>{{ $t(`L_${props.item.type}`) }}</td>
                <td class="cursor-pointer">
                  <a @click="getDocumentPDF(props.item)">{{ props.item.name }}</a>
                </td>
                <td>
                  <v-icon @click="downloadPDF(props.item)" color="#052f60" :icon="mdiDownload" />
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
        <v-card flat v-else>
          <v-card-text>
            <div class="py-5">
              <v-img
                id="documents-folder-img"
                height="180"
                width="180"
                src="@/assets/documentsFolder.png"
                alt="documents-folder"
                class="d-flex mx-auto"
              />
            </div>
            <div id="you-have-no-available" class="font-weight-bold text-center">
              {{ $t('L_no_documents_currently_available') }}
            </div>
          </v-card-text>
        </v-card>
      </v-card>
    </v-col>
    <v-col cols="12" class="container" />
  </v-row>
</template>

<script lang="ts">
import { openInNewWindow } from '@/mixins/openNewWindow';
import { formatDate as formatDateUtil } from '@/shared/util/formatters';
import { mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { defineComponent } from 'vue';
import { format } from 'date-fns';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { mdiDownload } from '@mdi/js';
import type { IGlobalStoreDocumentsItem } from '@/stores/global/globalStore.model';
import type { IDocumentMetadata } from '@/shared/api/models.content';

export default defineComponent({
  name: 'PolicyDocuments',
  mixins: [openInNewWindow],
  data() {
    return {
      mdiDownload,
    };
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      currentLocale: 'getLocale',
      policyDocuments: 'policyDocuments',
      base64String: 'base64String',
    }),
    headers() {
      return [
        {
          title: this.$t('L_date'),
          align: 'left',
          value: 'Date',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_type'),
          align: 'left',
          value: 'Type',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_name'),
          align: 'left',
          value: 'Name',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_download'),
          value: 'Download',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
      ];
    },
    docs() {
      const docs = this.policyDocuments;
      const mappedDocs = docs?.map((doc: IGlobalStoreDocumentsItem) => {
        const rightDoc = doc.labels
          ? doc.labels.find(
              (label: { language: string; name: string; type: string }) =>
                label.language === (this.currentLocale as string)
            )
          : null;
        const docMeta = {
          date: doc.dateReceived
            ? formatDateUtil(format(new Date(doc.dateReceived), 'yyyy-MM-dd'), this.dateFormat)
            : '',
          name: rightDoc ? `${rightDoc.name} - ${doc.contract}` : '',
          type: rightDoc ? rightDoc.type : '',
          doc: doc,
        };
        return docMeta;
      });
      return mappedDocs ?? [];
    },
  },
  methods: {
    async downloadPDF(item: {
      date: string;
      name: string;
      type: string;
      doc: IGlobalStoreDocumentsItem;
    }) {
      try {
        const _n = item.doc.name;
        const t = _n ? `view_doc_${_n[0].toLowerCase()}${_n.slice(1).replaceAll(' ', '')}` : '';
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      // * see https://dev.to/nombrekeff/download-file-from-blob-21ho
      // Create a link pointing to the ObjectURL containing the blob
      let blobURL;
      const docMetadata = item.doc.metadata ?? {
        opComp: '',
        opArea: '',
        contract: '',
        dateReceived: '',
      };
      if (item.doc.documentType === 'A') {
        blobURL = await this.getAllDocumentsUrl(docMetadata);
      } else {
        blobURL = await this.getSingleDocumentUrl(docMetadata);
      }
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = blobURL as string;
      tempLink.setAttribute('download', item.name);
      // Safari thinks _blank anchor are pop ups. We only want to set _blank
      // target if the browser does not support the HTML5 download attribute.
      // This allows you to download files in desktop safari if pop up blocking
      // is enabled.
      if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
      }
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    },
    async getDocumentPDF(item: {
      date: string;
      name: string;
      type: string;
      doc: IGlobalStoreDocumentsItem;
    }) {
      try {
        const _docName = item.doc.name;
        const t = _docName
          ? `view_doc_${_docName[0].toLowerCase()}${_docName.slice(1).replaceAll(' ', '')}`
          : '';
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      let url;
      const docMetadata = item.doc.metadata ?? {
        opComp: '',
        opArea: '',
        contract: '',
        dateReceived: '',
      };
      if (item.doc.documentType === 'A') {
        url = (await this.getAllDocumentsUrl(docMetadata)) as string;
      } else {
        url = (await this.getSingleDocumentUrl(docMetadata)) as string;
      }
      const isWeb = CapacitorFacade.isWeb();
      await this.openInNewWindow({ url, inappbrowser: !isWeb });
    },
    // ***
    getSingleDocumentUrl(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.globalStore
          .getOnePolicyDocument(requestObject)
          .then(() =>
            URL.createObjectURL(new Blob([this.base64String], { type: 'application/pdf' }))
          );
      } else {
        return this.globalStore.getOnePolicyDocument(requestObject).then(() => this.base64String);
      }
    },
    // ***
    getAllDocumentsUrl(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.globalStore
          .getAllPolicyDocuments(requestObject)
          .then(() =>
            URL.createObjectURL(new Blob([this.base64String], { type: 'application/pdf' }))
          );
      } else {
        return this.globalStore.getAllPolicyDocuments(requestObject).then(() => this.base64String);
      }
    },
  },
  beforeRouteEnter(_, __, next) {
    // called before the route that renders this component is confirmed.
    // does NOT have access to `this` component instance,
    // because it has not been created yet when this guard is called!
    next((vm) => {
      // access to component public instance via `vm`
      try {
        const i = vm.$dynatrace.enterAction('view_documentsPage');
        vm.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    });
  },
  beforeRouteUpdate() {
    // called when the route that renders this component has changed,
    // but this component is reused in the new route.
    try {
      const i = this.$dynatrace.enterAction('view_documentsPage');
      this.$dynatrace.leaveAction(i);
    } catch (_error) {
      /* empty */
    }
  },
});
</script>

<style lang="scss" scoped>
.header-back {
  width: 32px;
  height: 19px;
  margin: 0 83px 0 0;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #333;
}

table.v-table tbody td {
  font-size: 14px !important;
  padding: 24px !important;
}

.v-datatable.v-table thead th:first-child {
  padding-left: 24px !important;
}

#documents-note-text {
  border-bottom: solid 1px #ccc;
}

#please-note-verbiage {
  padding: 16px;
}
</style>
